import { TOP_MENU_CATALOG } from './submenu-catalog';
import { hasAnyRole } from '../role.util';
import { PermissionStore } from '@/pages/service/core/permission-store';

export interface NavTarget {
    routerLink: string[];
    queryParams?: Record<string, string>;
}

function urlOf(link: string[], qp?: Record<string, string>): string {
    const path = '/' + (link || []).join('/').replace(/^\/+/, '');
    if (qp) {
        const q = new URLSearchParams(qp).toString();
        return q ? path + '?' + q : path;
    }
    return path;
}

/**
 * The first top-level page the current user may open, in sidebar (catalog) order — used as the
 * "home" target (e.g. the topbar logo). Mirrors AppMenu.buildMenu's role + STRICT menuAllowed
 * filtering, so a restricted user whose permission template denies the default dashboard lands
 * on their first GRANTED page instead of being bounced to /notfound by the route guard.
 *
 * For an unrestricted user this reproduces the login landing convention naturally: the first
 * catalog entry their role can see (Company Admin → /lead-dashboard, agent → /my-work).
 * `settings` (account menu only) and `assistant` (bottom-pinned) are skipped as home targets.
 * Returns null only if nothing at all is reachable — the caller supplies a safe fallback.
 */
export function firstAllowedRoute(perms: PermissionStore, opts?: { proMode?: boolean }): NavTarget | null {
    const proMode = !!opts?.proMode;
    for (const entry of TOP_MENU_CATALOG) {
        if (entry.id === 'settings' || entry.id === 'assistant') continue;
        if (entry.roles && !hasAnyRole(...entry.roles)) continue;
        if (entry.proHidden && proMode) continue;

        if (entry.routerLink) {
            if (perms.menuAllowed(urlOf(entry.routerLink, entry.queryParams))) {
                return { routerLink: entry.routerLink, queryParams: entry.queryParams };
            }
            continue;
        }

        // Group entry: pick its first role-allowed, granted section item (what openGroup navigates to).
        for (const sec of entry.sections ?? []) {
            for (const it of sec.items ?? []) {
                if (it.roles && !hasAnyRole(...it.roles)) continue;
                if (it.proHidden && proMode) continue;
                if (perms.menuAllowed(urlOf(it.routerLink, it.queryParams))) {
                    return { routerLink: it.routerLink, queryParams: it.queryParams };
                }
            }
        }
    }
    return null;
}
